import { call, cancelled, put, select } from 'redux-saga/effects'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { selectPageConfigurationData } from '@/store/PageConfiguration/PageConfigurationSelectors'
import getAPIConfig from '@/shared/tools/getAPIConfig'
import { updateCharacteristicsModuleConfiguration } from '../../../SubscriptionFormConfigurationSlice'
import * as Sentry from '@sentry/react'

export const characteristicsModuleActions = {
  SEARCH_ADDRESS: 'SEARCH_ADDRESS',
  CITY_ELIGIBILITY_CHECK: 'CITY_ELIGIBILITY_CHECK',
  LOAD_TELECOM_SUPPLIERS: 'LOAD_TELECOM_SUPPLIERS',
}

export function* searchAddress({ address, callback }) {
  const controller = new AbortController()
  const fullUrl = `https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(address)}`

  try {
    const response = yield call(fetch, fullUrl, { signal: controller.signal })
    const isValid = response.status === 200
    const responseData = yield call([response, response.json])
    const results = responseData.features.map(result => {
      return {
        address: result.properties.street ? result.properties.street : (result.properties.name ?? ''),
        label: result.properties.label,
        city: result.properties.city,
        inseeCode: result.properties.citycode,
        zipcode: result.properties.postcode,
        houseNumber: result.properties.housenumber,
        type: result.properties.type,
        context: result.properties.context,
      }
    })
    callback(results, isValid)
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('searchAddressError'), { extra: { error } })
    }
  } finally {
    if (yield cancelled()) {
      controller.abort()
    }
  }
}

export function* cityEligibilityCheck({ inseeCode }) {
  const controller = new AbortController()
  const { authTokenSession, lang } = yield select(selectAuthData)
  const { eld_check_url: url } = yield select(selectPageConfigurationData)
  const fullUrl = `${url}?lang=${lang}&city=${encodeURIComponent(inseeCode)}&energy=dual`
  yield put(updateCharacteristicsModuleConfiguration({ cityEligibilityIsLoading: true }))

  try {
    const config = yield call(getAPIConfig, { method: 'get', authTokenSession })
    const signal = controller.signal
    const response = yield call(fetch, fullUrl, { ...config, signal })
    const isValid = response.status === 200
    const responseData = yield call([response, response.json])
    yield put(updateCharacteristicsModuleConfiguration({ cityEligibilityIsLoading: false }))
    if (!isValid) {
      if (import.meta.env.PROD && response.status !== 422) {
        Sentry.captureException(new Error('cityEligibilityCheckError'), {
          extra: { responseData, status: response.status },
        })
      }
      return
    }
    yield put(updateCharacteristicsModuleConfiguration({ cityEligibility: responseData }))
  } catch (error) {
    yield put(updateCharacteristicsModuleConfiguration({ cityEligibilityIsLoading: false, cityEligibility: {} }))
    if (import.meta.env.PROD) {
      Sentry.captureException(new Error('verifyZohoIdError'), { extra: { error } })
    }
  } finally {
    if (yield cancelled()) {
      controller.abort()
    }
  }
}

export function* loadTelecomSuppliers() {
  const { authTokenSession, lang } = yield select(selectAuthData)
  const { load_supplier_options_url: url } = yield select(selectPageConfigurationData)
  const fullUrl = `${url}?lang=${lang}&energy=telecom`

  try {
    const config = yield call(getAPIConfig, { method: 'get', authTokenSession })
    const response = yield call(fetch, fullUrl, config)
    const isValid = response.status === 200
    const responseData = yield call([response, response.json])
    if (!isValid) {
      if (import.meta.env.PROD && response.status !== 422) {
        Sentry.captureException(new Error('loadTelecomSupplierError'), {
          extra: { responseData, status: response.status },
        })
      }
      return
    }
    yield put(updateCharacteristicsModuleConfiguration({ suppliers: responseData }))
  } catch (error) {
    if (import.meta.env.PROD) {
      Sentry.captureException(error, { extra: { fullUrl } })
    }
  }
}
